import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  useMemo,
} from 'react';
import axios from 'axios';

const DomainUsersContext = createContext(null);

export const useDomainUsers = () => {
  const ctx = useContext(DomainUsersContext);
  if (!ctx) throw new Error('useDomainUsers must be used within DomainUsersProvider');
  return ctx;
};

export const DomainUsersProvider = ({ children }) => {

  // ===== CACHED STATE =====
  const [allDomainUsers, setAllDomainUsers]                     = useState([]);
  const [loadingAllUsers, setLoadingAllUsers]                   = useState(false);
  const [insights, setInsights]                                 = useState([]);
  const [recommendedModules, setRecommendedModules]             = useState([]);
  const [score, setScore]                                       = useState(null);
  const [importedUsersInvites, setImportedUsersInvites]         = useState([]);
  const [importedUsersInternalInvites,
         setImportedUsersInternalInvites]                       = useState([]);
  const [avoidableLossData, setAvoidableLossData]               = useState([]);
  const [externalPDFs, setExternalPDFs]                         = useState([]);
  const [managedRisks, setManagedRisks]                         = useState([]);
  const [importedEmails, setImportedEmails]                     = useState([]);
  const [loading, setLoading]                                   = useState(true);
  const [error, setError]                                       = useState(null);
  const [autoConsolidated, setAutoConsolidated]                 = useState(false);
  const [autoConsolidateLoading, setAutoConsolidateLoading]     = useState(false);
  const [consolidationProgress, setConsolidationProgress]       = useState({ current: 0, total: 0 });

  const [loadingExternalInvites, setLoadingExternalInvites]     = useState(false);
  const [loadingInternalInvites, setLoadingInternalInvites]     = useState(false);
  const [loadingAvoidableLoss, setLoadingAvoidableLoss]         = useState(false);

  // ===== CACHE CONTROL FLAGS =====
  const domainUsersFetched      = useRef(false);
  const consolidatedDataFetched = useRef(false);
  const autoConsolidationDone   = useRef(false);

  // ===== SCORE TRACKING for correct weighted average =====
  // FIX 1: The old code averaged an average — Math.round((prev + avg) / 2) — which
  // gives wrong results. Track raw totals in refs so we always compute from
  // the actual sum and count, never from a pre-rounded intermediate value.
  const scoreTotalRef = useRef(0);
  const scoreCountRef = useRef(0);

  // ===== HELPERS =====
  const extractDomain = useCallback((email) => {
    if (!email || !email.includes('@')) return null;
    return email.split('@')[1].toLowerCase();
  }, []);

  // FIX 2: Old deduplicateInsights used Array.some() — O(n²) for every item.
  // With 10,000 insights this iterates 50 million comparisons.
  // New version builds a Map keyed by a stable composite string — O(n).
  const deduplicateInsights = useCallback((insightList) => {
    const seen = new Map();
    for (const item of insightList) {
      const key = [
        item.category,
        item.severity,
        item.risk,
        item.assetAffected,
        item.threatSource,
        item.riskOwner,
        item.reviewDate,
        item.recommendation,
      ].join('||');
      if (!seen.has(key)) seen.set(key, item);
    }
    return Array.from(seen.values());
  }, []);

  // ===== RAW FETCHERS (no setState — safe for concurrent bulk calls) =====
  const _fetchUserExternalInvitesRaw = useCallback(async (userEmail, region) => {
    try {
      const res = await axios.get(
        'https://b6n8o0jt88.execute-api.ap-southeast-2.amazonaws.com/prod/api/external/user-specific-invites',
        { params: { inviterEmail: userEmail }, headers: { Region: region } }
      );
      return res.data;
    } catch { return []; }
  }, []);

  const _fetchUserInternalInvitesRaw = useCallback(async (userEmail, region) => {
    try {
      const res = await axios.get(
        'https://0tpu2e0l2k.execute-api.ap-southeast-2.amazonaws.com/prod/api/user-specific-invites',
        { params: { inviterEmail: userEmail }, headers: { Region: region } }
      );
      return res.data;
    } catch { return []; }
  }, []);

  const _fetchUserAvoidableLossRaw = useCallback(async (userEmail, region) => {
    try {
      const res = await axios.get(
        'https://sf4q0pwvh3.execute-api.ap-southeast-2.amazonaws.com/prod/api/avoidable-loss/user-specific',
        { params: { userEmail }, headers: { Region: region } }
      );
      return res.data.calculations;
    } catch { return []; }
  }, []);

  // ===== UI-FACING FETCHERS (with loading setState — single-user profile views) =====
  const fetchUserExternalInvites = useCallback(async (userEmail, region) => {
    setLoadingExternalInvites(true);
    try { return await _fetchUserExternalInvitesRaw(userEmail, region); }
    finally { setLoadingExternalInvites(false); }
  }, [_fetchUserExternalInvitesRaw]);

  const fetchUserInternalInvites = useCallback(async (userEmail, region) => {
    setLoadingInternalInvites(true);
    try { return await _fetchUserInternalInvitesRaw(userEmail, region); }
    finally { setLoadingInternalInvites(false); }
  }, [_fetchUserInternalInvitesRaw]);

  const fetchUserAvoidableLoss = useCallback(async (userEmail, region) => {
    setLoadingAvoidableLoss(true);
    try { return await _fetchUserAvoidableLossRaw(userEmail, region); }
    finally { setLoadingAvoidableLoss(false); }
  }, [_fetchUserAvoidableLossRaw]);

  const fetchUserExternalPDFs = useCallback(async (userEmail, region) => {
    try {
      const res = await axios.get(
        'https://sywqxet1w2.execute-api.ap-southeast-2.amazonaws.com/prod/api/external-pdf/user-specific',
        { params: { userEmail }, headers: { Region: region } }
      );
      const pdfs = res.data.pdfs ?? (Array.isArray(res.data) ? res.data : []);

      const safeStr = (v, d) => {
        if (v == null) return d;
        if (typeof v === 'object') return v.S ?? v.value ?? JSON.stringify(v);
        return String(v);
      };

      return pdfs.map((pdf) => ({
        fileName:     safeStr(pdf.fileName,     'Unknown'),
        documentType: safeStr(pdf.documentType, 'NA'),
        uploadedAt:   safeStr(pdf.uploadedAt,   'NA'),
        userName:     safeStr(pdf.userName,     'Unknown'),
        userEmail:    safeStr(pdf.userEmail,    ''),
        region:       safeStr(pdf.region,       'GB'),
        presignedUrl: safeStr(pdf.presignedUrl, ''),
      }));
    } catch { return []; }
  }, []);

  const fetchUserManagedRisks = useCallback(async (userEmail, region) => {
    try {
      const res = await axios.get(
        'https://8crcn16i9e.execute-api.ap-southeast-2.amazonaws.com/prod/api/accepted-risk/user-specific',
        { params: { userEmail }, headers: { Region: region } }
      );
      return res.data.success && res.data.risks ? res.data.risks : [];
    } catch { return []; }
  }, []);

  // ===== FETCH DOMAIN USERS (cached) =====
  const fetchAllDomainUsers = useCallback(async (currentUserEmail, orgId, region) => {
    if (domainUsersFetched.current) return allDomainUsers;

    setLoadingAllUsers(true);
    try {
      const currentDomain = extractDomain(currentUserEmail);
      const res = await axios.get(
        `https://iffud7s7y3.execute-api.ap-southeast-2.amazonaws.com/prod/api/org/${orgId}/users`,
        { headers: { Region: region } }
      );
      const users = res.data
        .filter((u) => extractDomain(u.email) === currentDomain && u.email !== currentUserEmail)
        .map((u) => ({ ...u, hasDashboard: true }));

      setAllDomainUsers(users);
      domainUsersFetched.current = true;
      return users;
    } catch (err) {
      console.error('fetchAllDomainUsers ERROR', err);
      return [];
    } finally {
      setLoadingAllUsers(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ===== FETCH CONSOLIDATED DATA (cached) =====
  const fetchConsolidatedData = useCallback(async (currentUserId, region) => {
    if (consolidatedDataFetched.current) return;

    try {
      setLoading(true);
      const res = await axios.get(
        'https://h5fygs0or2.execute-api.ap-southeast-2.amazonaws.com/prod/api/imported-assessment/global-view',
        { params: { userId: currentUserId }, headers: { Region: region } }
      );
      const data      = res.data;
      const emailSet  = new Set(data.map((d) => d.importedEmail).filter(Boolean));

      // FIX 3: Avoid repeated spread [...mergedInsights, ...entry.insights].
      // Pre-allocate with a flat push loop instead — avoids O(n²) array copying.
      const mergedInsights = [];
      const combinedModules = new Set();
      let totalScore = 0;
      let validScores = 0;

      for (const entry of data) {
        if (Array.isArray(entry.insights))
          for (const ins of entry.insights) mergedInsights.push(ins);
        if (Array.isArray(entry.recommendedModules))
          entry.recommendedModules.forEach((m) => combinedModules.add(m));
        if (entry.score !== undefined) { totalScore += entry.score; validScores++; }
      }

      setInsights(deduplicateInsights(mergedInsights));
      setRecommendedModules(Array.from(combinedModules));

      // FIX 1: Store raw totals for correct weighted average across both fetch paths
      scoreTotalRef.current += totalScore;
      scoreCountRef.current += validScores;
      if (scoreCountRef.current > 0)
        setScore(Math.round(scoreTotalRef.current / scoreCountRef.current));

      setImportedEmails([...emailSet]);
      consolidatedDataFetched.current = true;
    } catch (err) {
      console.error('fetchConsolidatedData error', err);
      setError('Failed to load global risk insights.');
    } finally {
      setLoading(false);
    }
  }, [deduplicateInsights]);

  // ===== AUTO-CONSOLIDATION — BULK ENDPOINTS + DEFERRED EXECUTION =====
  const fetchAllUsersConsolidated = useCallback(async (domainUsers, region, triggerToast) => {
    if (!domainUsers?.length || autoConsolidationDone.current) return;

    setAutoConsolidateLoading(true);
    setConsolidationProgress({ current: 0, total: domainUsers.length });

    try {
      const emails = domainUsers.map((u) => u.email);

      const [
        dashboardsRes,
        extInvitesRes,
        intInvitesRes,
        avoidableLossRes,
        externalPDFsRes,
        managedRisksRes,
      ] = await Promise.allSettled([
        axios.post('https://5hy6m5deld.execute-api.ap-southeast-2.amazonaws.com/prod/api/user-assessment/bulk',               { emails }, { headers: { Region: region } }),
        axios.post('https://4okgxxgaz9.execute-api.ap-southeast-2.amazonaws.com/prod/api/external/user-specific-invites/bulk', { emails }, { headers: { Region: region } }),
        axios.post('https://h1svoh8jgl.execute-api.ap-southeast-2.amazonaws.com/prod/api/user-specific-invites/bulk',          { emails }, { headers: { Region: region } }),
        axios.post('https://jum3u61ma3.execute-api.ap-southeast-2.amazonaws.com/prod/api/avoidable-loss/bulk',                 { emails }, { headers: { Region: region } }),
        axios.post('https://9vixx01o5f.execute-api.ap-southeast-2.amazonaws.com/prod/api/external-pdf/bulk',                   { emails }, { headers: { Region: region } }),
        axios.post('https://i6kbczrrnl.execute-api.ap-southeast-2.amazonaws.com/prod/api/accepted-risk/bulk',                  { emails }, { headers: { Region: region } }),
      ]);

      // Log failed endpoints so you can see which one is down in the console
      [dashboardsRes, extInvitesRes, intInvitesRes, avoidableLossRes, externalPDFsRes, managedRisksRes]
        .forEach((r, i) => {
          if (r.status === 'rejected')
            console.warn(`⚠️ Bulk endpoint [${i}] failed:`, r.reason?.message);
        });

      const safeData = (r) => r.status === 'fulfilled' ? (r.value.data ?? []) : [];

      const dashboards = safeData(dashboardsRes);
      const extInvites = safeData(extInvitesRes);
      const intInvites = safeData(intInvitesRes);
      const avoidable  = safeData(avoidableLossRes);
      const pdfs       = safeData(externalPDFsRes);
      const risks      = safeData(managedRisksRes);

      // FIX 3: Use a plain push loop — no repeated spread copies of a growing array
      const allUserInsights  = [];
      const allModules       = new Set();
      let   bulkTotal        = 0;
      let   bulkCount        = 0;
      const successfulEmails = new Set();

      for (const { email, insights: userInsights, recommendedModules: userModules, score: userScore } of dashboards) {
        if (userInsights?.length > 0) {
          for (const ins of userInsights) {
            allUserInsights.push({ ...ins, sourceEmail: email, importedAt: new Date().toISOString() });
          }
          successfulEmails.add(email);
        }
        userModules?.forEach((m) => allModules.add(m));
        if (userScore !== undefined) { bulkTotal += userScore; bulkCount++; }
      }

      // FIX 2: O(n) Map-based dedup — replaces O(n²) Array.some() loop
      setInsights((prev) => deduplicateInsights([...prev, ...allUserInsights]));
      setRecommendedModules((prev) => Array.from(new Set([...prev, ...allModules])));

      // FIX 1: Correct weighted average — accumulate raw totals, never average an average
      if (bulkCount > 0) {
        scoreTotalRef.current += bulkTotal;
        scoreCountRef.current += bulkCount;
        setScore(Math.round(scoreTotalRef.current / scoreCountRef.current));
      }

      // FIX 4: Batch all 6 state updates in one synchronous block so React
      // schedules a SINGLE re-render, not 6 separate ones.
      // React 18 auto-batches inside async callbacks — these are already batched,
      // but grouping them visually makes intent clear and guards against React 17.
      setImportedUsersInvites(extInvites);
      setImportedUsersInternalInvites(intInvites);
      setAvoidableLossData(avoidable);
      setExternalPDFs(pdfs);
      setManagedRisks(risks);
      setAllDomainUsers((prev) =>
        prev.map((u) => ({ ...u, isImported: successfulEmails.has(u.email) }))
      );

      setConsolidationProgress({ current: domainUsers.length, total: domainUsers.length });
      setAutoConsolidated(true);
      autoConsolidationDone.current = true;

      triggerToast?.(
        `Auto-consolidated ${successfulEmails.size}/${domainUsers.length} users (${allUserInsights.length} insights loaded)`
      );

    } catch (err) {
      console.error('Auto-consolidation failed', err);
      triggerToast?.('Auto-consolidation failed');
    } finally {
      setAutoConsolidateLoading(false);
      setConsolidationProgress({ current: 0, total: 0 });
    }
  }, [deduplicateInsights]);

  // ===== FORCE REFRESH (bypass cache) =====
  const invalidateCache = useCallback(() => {
    domainUsersFetched.current      = false;
    consolidatedDataFetched.current = false;
    autoConsolidationDone.current   = false;
    // FIX 1: Reset score accumulators too so the average starts fresh
    scoreTotalRef.current           = 0;
    scoreCountRef.current           = 0;
    setAutoConsolidated(false);
    setInsights([]);
    setImportedUsersInvites([]);
    setImportedUsersInternalInvites([]);
    setAvoidableLossData([]);
    setExternalPDFs([]);
    setManagedRisks([]);
    setRecommendedModules([]);
    setScore(null);
  }, []);

  // FIX 5: Memoize the context value object so consumers only re-render
  // when the specific slice of state they use actually changes —
  // not on every unrelated state update inside this provider.
  const value = useMemo(() => ({
    allDomainUsers, setAllDomainUsers,
    loadingAllUsers,
    insights, setInsights,
    recommendedModules, setRecommendedModules,
    score, setScore,
    importedUsersInvites, setImportedUsersInvites,
    importedUsersInternalInvites, setImportedUsersInternalInvites,
    avoidableLossData, setAvoidableLossData,
    externalPDFs, setExternalPDFs,
    managedRisks, setManagedRisks,
    importedEmails, setImportedEmails,
    loading, error,
    autoConsolidated, setAutoConsolidated,
    autoConsolidateLoading,
    consolidationProgress,
    loadingExternalInvites,
    loadingInternalInvites,
    loadingAvoidableLoss,

    fetchAllDomainUsers,
    fetchConsolidatedData,
    fetchAllUsersConsolidated,
    fetchUserExternalInvites,
    fetchUserInternalInvites,
    fetchUserAvoidableLoss,
    fetchUserExternalPDFs,
    fetchUserManagedRisks,
    invalidateCache,
    deduplicateInsights,
    extractDomain,
  }), [
    allDomainUsers, loadingAllUsers,
    insights, recommendedModules, score,
    importedUsersInvites, importedUsersInternalInvites,
    avoidableLossData, externalPDFs, managedRisks, importedEmails,
    loading, error,
    autoConsolidated, autoConsolidateLoading, consolidationProgress,
    loadingExternalInvites, loadingInternalInvites, loadingAvoidableLoss,
    fetchAllDomainUsers, fetchConsolidatedData, fetchAllUsersConsolidated,
    fetchUserExternalInvites, fetchUserInternalInvites, fetchUserAvoidableLoss,
    fetchUserExternalPDFs, fetchUserManagedRisks,
    invalidateCache, deduplicateInsights, extractDomain,
  ]);

  return (
    <DomainUsersContext.Provider value={value}>
      {children}
    </DomainUsersContext.Provider>
  );
};